/**
 * Countries accepted by the application.
 *
 * The member name is the ISO 3166-1 alpha-2 code and the numeric value is what the API sends and stores,
 * so it must match `Domain.Enums.Country` in the backend exactly. Never renumber existing members; new
 * countries are appended after UNKNOWN.
 */
export enum Country {
    NL = 0,
    BE = 1,
    FR = 2,
    DE = 3,
    LU = 4,
    UNKNOWN = 5,

    // Remaining European countries, alphabetical by ISO code. Appended in Sept 2026.
    AD = 6,
    AL = 7,
    AT = 8,
    BA = 9,
    BG = 10,
    BY = 11,
    CH = 12,
    CY = 13,
    CZ = 14,
    DK = 15,
    EE = 16,
    ES = 17,
    FI = 18,
    GB = 19,
    GR = 20,
    HR = 21,
    HU = 22,
    IE = 23,
    IS = 24,
    IT = 25,
    LI = 26,
    LT = 27,
    LV = 28,
    MC = 29,
    MD = 30,
    ME = 31,
    MK = 32,
    MT = 33,
    NO = 34,
    PL = 35,
    PT = 36,
    RO = 37,
    RS = 38,
    RU = 39,
    SE = 40,
    SI = 41,
    SK = 42,
    SM = 43,
    TR = 44,
    UA = 45,
    VA = 46,
    XK = 47,
}

/** Background colour of a country's standard passenger-car plate; used by the license plate renderer. */
export type PlateBackground = 'yellow' | 'white';

export interface CountryDefinition {
    value: Country;
    /** ISO 3166-1 alpha-2 code; always equal to the enum member name. */
    iso2: string;
    /** i18n key of the translated country name (`core.country.<iso2>`). */
    labelKey: string;
    /** International vehicle registration code shown on the blue strip of the plate (B, D, F, ...). */
    plateCode: string;
    plateBackground: PlateBackground;
}

const define = (value: Country, plateCode: string, plateBackground: PlateBackground = 'white'): CountryDefinition => {
    const iso2 = Country[value];
    return { value, iso2, labelKey: `core.country.${iso2.toLowerCase()}`, plateCode, plateBackground };
};

/**
 * Single source of truth for the accepted countries on the UI side. Dropdown options, the Google Places
 * mapping, the plate renderer and the phone-prefix pickers all derive from this list. UNKNOWN is
 * deliberately absent; it is only added to `CountryOptions`.
 *
 * Order: core markets first, then the rest of Europe alphabetically by ISO code. This is also the order
 * of the country dropdown.
 */
export const COUNTRIES: readonly CountryDefinition[] = [
    define(Country.NL, 'NL', 'yellow'),
    define(Country.BE, 'B'),
    define(Country.DE, 'D'),
    define(Country.FR, 'F'),
    define(Country.LU, 'L', 'yellow'),

    define(Country.AD, 'AND'),
    define(Country.AL, 'AL'),
    define(Country.AT, 'A'),
    define(Country.BA, 'BIH'),
    define(Country.BG, 'BG'),
    define(Country.BY, 'BY'),
    define(Country.CH, 'CH'),
    define(Country.CY, 'CY'),
    define(Country.CZ, 'CZ'),
    define(Country.DK, 'DK'),
    define(Country.EE, 'EST'),
    define(Country.ES, 'E'),
    define(Country.FI, 'FIN'),
    define(Country.GB, 'UK'),
    define(Country.GR, 'GR'),
    define(Country.HR, 'HR'),
    define(Country.HU, 'H'),
    define(Country.IE, 'IRL'),
    define(Country.IS, 'IS'),
    define(Country.IT, 'I'),
    define(Country.LI, 'FL'),
    define(Country.LT, 'LT'),
    define(Country.LV, 'LV'),
    define(Country.MC, 'MC'),
    define(Country.MD, 'MD'),
    define(Country.ME, 'MNE'),
    define(Country.MK, 'NMK'),
    define(Country.MT, 'M'),
    define(Country.NO, 'N'),
    define(Country.PL, 'PL'),
    define(Country.PT, 'P'),
    define(Country.RO, 'RO'),
    define(Country.RS, 'SRB'),
    define(Country.RU, 'RUS'),
    define(Country.SE, 'S'),
    define(Country.SI, 'SLO'),
    define(Country.SK, 'SK'),
    define(Country.SM, 'RSM'),
    define(Country.TR, 'TR'),
    define(Country.UA, 'UA'),
    define(Country.VA, 'V'),
    define(Country.XK, 'RKS'),
];

const byValue = new Map<Country, CountryDefinition>(COUNTRIES.map(d => [d.value, d]));
const byIso2 = new Map<string, CountryDefinition>(COUNTRIES.map(d => [d.iso2, d]));

/** The definition of a known country, or undefined for UNKNOWN, null/undefined and unregistered values. */
export const getCountryDefinition = (country: Country | null | undefined): CountryDefinition | undefined =>
    country == null ? undefined : byValue.get(country);

/** ISO 3166-1 alpha-2 code of a known country, or undefined for UNKNOWN. */
export const countryToIso2 = (country: Country | null | undefined): string | undefined =>
    getCountryDefinition(country)?.iso2;

/** Maps an ISO 3166-1 alpha-2 code (case-insensitive) to the enum; anything unrecognised is UNKNOWN. */
export const countryFromIso2 = (iso2: string | null | undefined): Country =>
    (iso2 ? byIso2.get(iso2.toUpperCase())?.value : undefined) ?? Country.UNKNOWN;

/** ISO codes of every accepted country, in registry order. */
export const CountryIso2Codes: readonly string[] = COUNTRIES.map(d => d.iso2);

/** Dropdown options; labels are i18n keys, translate them with `translateOptions`. */
export const CountryOptions = [
    { value: Country.UNKNOWN, label: 'core.country.unknown' },
    ...COUNTRIES.map(d => ({ value: d.value, label: d.labelKey })),
];
